import React, { ReactNode, useLayoutEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";

/**
 * A single-line row that stays put while its content fits, and glides
 * sideways in a continuous loop once the content is wider than the row.
 * Hovering pauses it. Honours `prefers-reduced-motion` (the row then simply
 * clips). Used for the chip rows on the nutrition plan's meal cards.
 *
 * The loop is seamless because the content is rendered twice, each copy
 * padded by the gap, and the track slides by exactly one copy.
 */

/** Pixels per second the content travels. */
const SPEED = 40;
const MIN_DURATION_S = 8;

const slide = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const Viewport = styled.div`
  width: 100%;
  min-width: 0;
  overflow: hidden;

  &:hover > * {
    animation-play-state: paused;
  }
`;

const Track = styled.div<{ $duration: number; $animate: boolean }>`
  display: flex;
  width: max-content;
  ${({ $animate, $duration }) =>
    $animate &&
    css`
      animation: ${slide} ${$duration}s linear infinite;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Copy = styled.div<{ $gap: number }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${({ $gap }) => $gap}px;
  padding-right: ${({ $gap }) => $gap}px;
`;

const MarqueeRow = ({
  children,
  gap = 8,
}: {
  children: ReactNode;
  /** Space between the items, and between the end of the row and its repeat. */
  gap?: number;
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(0);

  // Measure after every layout change: the row animates only while its
  // content is wider than the viewport, and the speed follows the width.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const copy = copyRef.current;
    if (!viewport || !copy) return;
    const measure = () => {
      const contentWidth = copy.scrollWidth;
      setOverflow(contentWidth > viewport.clientWidth + 1 ? contentWidth : 0);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(copy);
    return () => observer.disconnect();
  }, [children]);

  const animate = overflow > 0;
  const duration = Math.max(MIN_DURATION_S, (overflow + gap) / SPEED);

  return (
    <Viewport ref={viewportRef}>
      <Track $animate={animate} $duration={duration}>
        <Copy ref={copyRef} $gap={gap}>
          {children}
        </Copy>
        {animate && (
          <Copy $gap={gap} aria-hidden="true">
            {children}
          </Copy>
        )}
      </Track>
    </Viewport>
  );
};

export default MarqueeRow;
